(() => {
  'use strict';
  const query = (s, c = document) => c.querySelector(s);
  const queryAll = (s, c = document) => [...c.querySelectorAll(s)];
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal ---------- */
  const reveal = scope => {
    const items = queryAll('[data-sr-reveal]', scope);
    if (reduced) { items.forEach(i => i.classList.add('is-visible')); return; }
    const io = new IntersectionObserver(es => es.forEach(({ isIntersecting, target }) => {
      if (isIntersecting) { target.classList.add('is-visible'); io.unobserve(target); }
    }), { threshold: 0.15 });
    items.forEach(i => io.observe(i));
  };

  /* ---------- counters ---------- */
  const counters = scope => queryAll('[data-sr-count]', scope).forEach(el => {
    if (el.dataset.srCountReady) return;
    el.dataset.srCountReady = 'true';
    const target = parseInt(el.dataset.srCount, 10) || 0;
    const pad = parseInt(el.dataset.srPad, 10) || 1;
    if (reduced) { el.textContent = String(target).padStart(pad, '0'); return; }
    const io = new IntersectionObserver(es => es.forEach(en => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      const start = performance.now(), dur = 2000;
      const tick = now => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = String(Math.round(target * eased)).padStart(pad, '0');
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }), { threshold: 0.4 });
    io.observe(el);
  });

  /* ---------- ajax cart ---------- */
  const forms = scope => queryAll('[data-sr-product-form]', scope).forEach(form => {
    if (form.dataset.srReady) return;
    form.dataset.srReady = 'true';
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const submitter = event.submitter;
      const status = query('[data-sr-status]', form);
      const button = submitter || query('[type=submit]', form);
      if (button.disabled) return;
      button.disabled = true;
      if (status) { status.hidden = false; status.textContent = 'Adding to bag\u2026'; }
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(new FormData(form).get('id')), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Cart request failed');
        if (status) status.textContent = 'Added to bag';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (submitter && submitter.dataset.srBuyNow === 'true') window.location.href = root + 'cart';
      } catch (_) {
        if (status) status.textContent = 'Unable to add. Please try again.';
      } finally { button.disabled = false; }
    }, { passive: false });
  });

  /* ---------- scroll film ---------- */
  const film = scope => queryAll('[data-sr-film]', scope).forEach(filmEl => {
    if (filmEl.dataset.srFilmReady) return;
    filmEl.dataset.srFilmReady = 'true';

    if (reduced) { filmEl.classList.add('sr-film--static'); return; }

    const q = s => query(s, filmEl);
    const stage = q('[data-sr-stage]');
    const bar = q('[data-sr-bar]');
    const progressEl = q('.sr-film__progress');
    const layers = {
      doors: q('[data-sr-layer="doors"]'), inside: q('[data-sr-layer="inside"]'),
      fabric: q('[data-sr-layer="fabric"]'), cut: q('[data-sr-layer="cut"]'),
      thread: q('[data-sr-layer="thread"]'), label: q('[data-sr-layer="label"]'),
      final: q('[data-sr-layer="final"]')
    };
    const img = k => layers[k] ? query('img', layers[k]) : null;
    const dl = q('.sr-film__dhalf--l'), dr = q('.sr-film__dhalf--r');
    const hint = q('.sr-film__hint');
    const doorCap = layers.doors ? query('.sr-film__cap', layers.doors) : null;
    const seam = q('[data-sr-seam]');
    const title = q('.sr-film__title');
    const shade = q('.sr-film__shade');
    const product = q('.sr-film__product');
    const cta = q('.sr-film__cta');

    /* preload */
    queryAll('img', filmEl).forEach(i => { const p = new Image(); p.src = i.currentSrc || i.src; });

    let seamLen = 0;
    if (seam) { seamLen = seam.getTotalLength(); seam.style.strokeDasharray = seamLen; seam.style.strokeDashoffset = seamLen; }

    const clamp = v => Math.max(0, Math.min(1, v));
    const ease = t => t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const ph = (p, a, b) => clamp((p - a) / (b - a));
    const fade = (p, a, b, c, d) => ease(ph(p, a, b)) * (1 - ease(ph(p, c, d)));
    const setScale = (k, v, extra) => { const i = img(k); if (i) i.style.transform = 'scale(' + v + ')' + (extra || ''); };
    const setOpacity = (k, v) => { if (layers[k]) layers[k].style.opacity = v; };

    let ticking = false;
    const render = () => {
      ticking = false;
      const rect = filmEl.getBoundingClientRect();
      const total = filmEl.offsetHeight - innerHeight;
      if (total <= 0) return;
      const p = clamp(-rect.top / total);
      const before = rect.top > 0, done = p >= 1;
      const hidden = before || done;
      if (stage) { stage.style.visibility = hidden ? 'hidden' : 'visible'; }
      if (progressEl) { progressEl.style.visibility = hidden ? 'hidden' : 'visible'; }
      const so = 1 - ease(ph(p, .97, 1));
      if (stage) stage.style.opacity = so;
      if (progressEl) progressEl.style.opacity = so;
      if (bar) bar.style.height = (p * 100) + '%';

      const a = ease(ph(p, 0, .13));
      if (dl) dl.style.transform = 'translateX(' + (-a * 103) + '%)';
      if (dr) dr.style.transform = 'translateX(' + (a * 103) + '%)';
      if (hint) hint.style.opacity = 1 - a * 2.5;
      if (doorCap) doorCap.style.opacity = 1 - ease(ph(p, .09, .14));
      setOpacity('doors', 1 - ease(ph(p, .13, .17)));

      setOpacity('inside', 1 - ease(ph(p, .24, .30)));
      setScale('inside', 1.04 + ease(ph(p, .02, .28)) * .30);

      setOpacity('fabric', fade(p, .26, .31, .40, .46));
      setScale('fabric', 1.12 - ease(ph(p, .26, .46)) * .10);

      setOpacity('cut', fade(p, .42, .47, .56, .62));
      setScale('cut', 1.05 + ease(ph(p, .42, .62)) * .07, ' translateX(' + (1.5 - ease(ph(p, .42, .62)) * 3) + '%)');

      setOpacity('thread', fade(p, .58, .63, .72, .78));
      setScale('thread', 1.10 - ease(ph(p, .58, .78)) * .08);
      if (seam) seam.style.strokeDashoffset = seamLen * (1 - ease(ph(p, .62, .72)));

      setOpacity('label', fade(p, .74, .79, .87, .92));
      const zl = ease(ph(p, .74, .92));
      const li = img('label');
      if (li) { li.style.transform = 'scale(' + (1 + zl * .20) + ')'; li.style.transformOrigin = '52% 46%'; }

      const fi = ease(ph(p, .90, .975));
      setOpacity('final', fi);
      setScale('final', 1.08 - fi * .08);
      if (shade) shade.style.opacity = fi * .85;
      if (title) { title.style.letterSpacing = (0.30 * (1 - fi)) + 'em'; title.style.transform = 'translateY(' + (26 * (1 - fi)) + 'px)'; }
      const co = ease(ph(p, .94, .985));
      if (product) product.style.opacity = 0.85 * co;
      if (cta) { cta.style.opacity = co; cta.style.pointerEvents = co > .5 ? 'auto' : 'none'; }
    };

    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(render); } }, { passive: true });
    addEventListener('resize', render);
    render();
  });

  const start = scope => { reveal(scope); counters(scope); forms(scope); film(scope); };
  document.addEventListener('shopify:section:load', e => start(e.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(() => start(document)));
  } else { requestAnimationFrame(() => start(document)); }
})();
